import { useEffect, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import DepartmentPills, { PILLAR_META, BAND_COLORS } from "@/components/DepartmentPills";

/**
 * PlayerVerdictHero — the personal department verdict for the athlete.
 *
 * Same data layer the coach sees. No information asymmetry. The athlete
 * learns to read their own three pillars across a career.
 */
export default function PlayerVerdictHero() {
  const [v, setV] = useState(null);
  const [showWhy, setShowWhy] = useState(false);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/department/verdict/me"); setV(data); }
      catch { setV(false); }
    })();
  }, []);

  if (v === false) return null;   // silently hide if unavailable
  if (v == null) {
    return (
      <div className="rounded-2xl ink-card-elevated p-5 flex items-center gap-3" data-testid="player-verdict-loading">
        <Loader2 className="h-4 w-4 animate-spin text-white/40" />
        <span className="text-sm text-white/55">Reading your department verdict…</span>
      </div>
    );
  }
  const overall = BAND_COLORS[v.overall_band] || BAND_COLORS.hold;
  return (
    <div className="rounded-2xl ink-card-elevated p-5 relative overflow-hidden" data-testid="player-verdict-hero">
      <div className="absolute top-0 right-0 bottom-0 w-1.5" style={{ backgroundColor: overall.text }} />
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--brand-secondary)" }}>
            <Sparkles className="h-3 w-3" /> Your intelligence layer · today
          </div>
          <div className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-1" style={{ color: overall.text }} data-testid="player-verdict-band">
            {overall.label} · {Math.round(v.overall_score)}
          </div>
          <div className="text-[12px] text-white/55 mt-1.5 max-w-xl">
            ArmIQ runs three pillars on every athlete every day. Worst pillar wins — because safety always wins.
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <DepartmentPills verdict={v} testId="player-verdict-pills" />
          <button
            onClick={() => setShowWhy(!showWhy)}
            className="text-[10px] uppercase tracking-wider font-bold text-white/55 hover:text-white"
            data-testid="player-verdict-why-toggle"
          >
            {showWhy ? "Hide why" : "Why this score?"}
          </button>
        </div>
      </div>
      {showWhy && (
        <div className="mt-4 grid md:grid-cols-3 gap-3" data-testid="player-verdict-detail">
          {["availability", "development", "performance"].map((k) => {
            const p = v.pillars[k];
            const m = PILLAR_META[k];
            const Icon = m.Icon;
            return (
              <div key={k} className="rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`player-verdict-pillar-${k}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded flex items-center justify-center" style={{ backgroundColor: `${m.color}1c`, color: m.color }}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div>
                    <div className="text-xs font-bold text-white">{m.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-white/40">{p.band} · {Math.round(p.score)}</div>
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {(p.drivers || []).slice(0, 5).map((d, i) => (
                    <li key={i} className="text-[11px] text-white/70 flex items-start justify-between gap-2">
                      <span>{d.signal}</span>
                      <span className={`font-mono-num text-[10px] font-bold ${d.delta > 0 ? "text-emerald-300" : d.delta < 0 ? "text-amber-300" : "text-white/35"}`}>
                        {d.delta > 0 ? "+" : ""}{d.delta}
                      </span>
                    </li>
                  ))}
                  {(p.drivers || []).length === 0 && <li className="text-[11px] text-white/40">No active drivers.</li>}
                </ul>
              </div>
            );
          })}
          {v.cause_analysis?.length > 0 && (
            <div className="md:col-span-3 rounded-lg border p-3"
                 style={{ borderColor: "rgba(217,4,41,0.35)", backgroundColor: "rgba(217,4,41,0.06)" }}
                 data-testid="player-verdict-cause">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: "var(--brand-secondary)" }}>
                Cause analysis · the why behind the why
              </div>
              {v.cause_analysis.map((c) => (
                <div key={c.rule} className="text-sm mb-3 last:mb-0">
                  <div className="font-bold text-white">{c.title}</div>
                  <div className="text-[12px] text-white/75 leading-relaxed mt-1">{c.narrative}</div>
                  <div className="text-[11px] text-white/60 mt-1.5">
                    <b style={{ color: "var(--brand-secondary)" }}>Next step:</b> {c.next_step}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
